import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Trophy, BarChart3, Flame } from "lucide-react";
import { API } from "@/App";
import { apiFetch, setSessionExpiry } from "@/lib/apiClient";

const WORDS = ["Day", "Week", "Month", "Year", "LIFE!"];

const getApiErrorMessage = (payload) => {
  if (!payload) return null;

  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  if (Array.isArray(payload.detail) && payload.detail.length > 0) {
    const firstDetail = payload.detail[0];
    if (typeof firstDetail === "string" && firstDetail.trim()) {
      return firstDetail;
    }

    if (firstDetail && typeof firstDetail === "object") {
      const location = Array.isArray(firstDetail.loc)
        ? firstDetail.loc.filter((part) => part !== "body").join(".")
        : "";
      const message = typeof firstDetail.msg === "string" ? firstDetail.msg : "Invalid input";
      return location ? `${location}: ${message}` : message;
    }
  }

  if (typeof payload.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  return null;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [wordIndex, setWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await apiFetch('/auth/me', {
          skipUnauthorizedRedirect: true,
        });
        if (response.ok) {
          const payload = await response.json();
          setSessionExpiry(payload.session_expires_at);
          navigate('/');
        }
      } catch (error) {
        // Not authenticated, stay on login
      }
    };
    checkAuth();
  }, [navigate]);

  // Show error from Google OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error")) {
      setError("Google sign-in failed. Please try again or use email/password.");
    }
  }, [location.search]);

  // Word cycling animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % WORDS.length);
        setIsAnimating(false);
      }, 200);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleGoogleSignIn = () => {
    window.location.href = `${API}/auth/google`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "register" && password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { email, password, name }
        : { email, password };

      const response = await apiFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        skipUnauthorizedRedirect: true,
        body: JSON.stringify(body),
      });

      let data = null;
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        data = await response.json();
      }

      if (!response.ok) {
        const message = getApiErrorMessage(data);
        setError(message || "Something went wrong. Please try again.");
        return;
      }

      if (!data) {
        setError("Unexpected server response. Please try again.");
        return;
      }

      setSessionExpiry(data.session_expires_at);
      navigate("/", { replace: true, state: { user: data.user } });
    } catch (err) {
      if (mode === "login") {
        setError("Invalid email or password");
      } else {
        setError("Unable to create account. Please check your details and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Calendar, title: "365 Days", desc: "Track every proud moment" },
    { icon: Trophy, title: "Earn Trophies", desc: "Build streaks" },
    { icon: BarChart3, title: "Statistics", desc: "See your progress" },
    { icon: Flame, title: "Stay Consistent", desc: "Build habits" }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-4">
            Proud Every Day
          </h1>
          
          {/* Animated Tagline */}
          <p className="text-lg text-muted-foreground">
            Win Your{" "}
            <span 
              className={`inline-block text-primary font-semibold transition-all duration-200 ${
                isAnimating ? 'opacity-0 transform -translate-y-2' : 'opacity-100 transform translate-y-0'
              }`}
            >
              {WORDS[wordIndex]}
            </span>
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="feature-card flex items-center gap-3"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <feature.icon className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{feature.title}</p>
                <p className="text-xs text-muted-foreground truncate">{feature.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Email / Password Form */}
        <form onSubmit={handleSubmit} className="space-y-3 mb-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Please wait…" : mode === "register" ? "Create Account" : "Sign In"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground mb-4">
          {mode === "login" ? (
            <>
              Don't have an account?{" "}
              <button type="button" className="text-primary underline" onClick={() => { setMode("register"); setError(""); }}>
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button type="button" className="text-primary underline" onClick={() => { setMode("login"); setError(""); }}>
                Sign in
              </button>
            </>
          )}
        </p>

        {/* <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div> */}

        {/* Google Sign In Button */}
        {/* <Button 
          type="button"
          onClick={handleGoogleSignIn}
          className="google-btn w-full"
          data-testid="google-signin-btn"
          variant="outline"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </Button> */}

        {/* Terms */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
